/**
 * NotificationsTab — /settings/notifications
 * One form section per alert channel (enable switch, its fields, a test button) and one save. Demo page: /profile
 * (notification settings). Template example: NextAdmin `profile/notification/page.tsx` and `profile/account/page.tsx`.
 * Server routes: getNotificationConfig / updateNotificationConfig / testNotification, all `CanManageNotifications`.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  getNotificationConfigOptions,
  getNotificationConfigQueryKey,
  testNotificationMutation,
  updateNotificationConfigMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type { NotificationConfig } from '../../generated/api/types.gen.js';
import { zNotificationTestCreate } from '../../generated/api/zod.gen.js';
import {
  Form,
  FormActions,
  FormFullWidth,
  FormSection,
  NumberFormField,
  SwitchFormField,
  TextFormField,
  useZodForm,
} from '../../components/form/index.js';
import { Can } from '../../components/auth/Can.js';
import { Button } from '../../components/ui/Button.js';
import { QueryBoundary } from '../../components/ui/QueryBoundary.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { useToast } from '../../components/ui/Toast.js';
import {
  type NotificationsValues,
  changedChannels,
  notificationsSchema,
  savedHint,
  toFormValues,
  toRequest,
} from './notifications-form.js';

type Channel = NonNullable<z.output<typeof zNotificationTestCreate>['channel']>;

function TestChannelButton({ channel, label }: { channel: Channel; label: string }) {
  const { notify } = useToast();
  const test = useMutation(testNotificationMutation());
  const run = () =>
    test.mutate(
      { body: { channel } },
      {
        onSuccess: () => notify(`Test sent to ${label}.`),
        onError: (error) => notify(error.message, 'danger'),
      },
    );
  return (
    <FormFullWidth>
      <Can policy="CanManageNotifications">
        <Button type="button" variant="secondary" loading={test.isPending} onClick={run}>
          Send a test to {label}
        </Button>
      </Can>
    </FormFullWidth>
  );
}

function NotificationsForm({ config }: { config: NotificationConfig }) {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const save = useMutation(updateNotificationConfigMutation());
  const [baseline, setBaseline] = React.useState(() => toFormValues(config));
  const form = useZodForm(notificationsSchema, { defaultValues: baseline });

  const submit = async (values: NotificationsValues) => {
    await save.mutateAsync({ body: toRequest(values, changedChannels(values, baseline)) });
    await queryClient.invalidateQueries({ queryKey: getNotificationConfigQueryKey() });
    // The saved credentials are now behind their hints; the fields go back to unset.
    const saved: NotificationsValues = {
      ...values,
      telegram: { ...values.telegram, botToken: undefined },
      discord: { ...values.discord, webhookUrl: undefined },
      email: { ...values.email, pass: undefined },
      webhook: { ...values.webhook, url: undefined },
    };
    setBaseline(saved);
    form.reset(saved);
    notify('Notification settings saved.');
  };

  return (
    <Form form={form} onSubmit={submit} guardUnsaved className="space-y-10">
      <FormSection title="Telegram" description="Send alerts to a Telegram chat through your bot.">
        <FormFullWidth>
          <SwitchFormField name="telegram.enabled" label="Send alerts to Telegram" />
        </FormFullWidth>
        <TextFormField
          name="telegram.botToken"
          label="Bot token"
          type="password"
          autoComplete="off"
          description={savedHint(config.telegram?.botToken)}
        />
        <TextFormField name="telegram.chatId" label="Chat" description="The chat identifier your bot posts to." />
        <TestChannelButton channel="telegram" label="Telegram" />
      </FormSection>

      <FormSection title="Discord" description="Post alerts to a Discord channel.">
        <FormFullWidth>
          <SwitchFormField name="discord.enabled" label="Send alerts to Discord" />
        </FormFullWidth>
        <FormFullWidth>
          <TextFormField
            name="discord.webhookUrl"
            label="Webhook URL"
            type="url"
            autoComplete="off"
            description={savedHint(config.discord?.webhookUrl)}
          />
        </FormFullWidth>
        <TestChannelButton channel="discord" label="Discord" />
      </FormSection>

      <FormSection title="Email" description="Send alerts through your own mail server.">
        <FormFullWidth>
          <SwitchFormField name="email.enabled" label="Send alerts by email" />
        </FormFullWidth>
        <TextFormField name="email.host" label="Mail server" placeholder="smtp.example.com" />
        <NumberFormField
          name="email.port"
          label="Port"
          minValue={1}
          maxValue={65535}
          formatOptions={{ useGrouping: false }}
        />
        <TextFormField name="email.user" label="Username" autoComplete="off" />
        <TextFormField
          name="email.pass"
          label="Password"
          type="password"
          autoComplete="new-password"
          description={savedHint(config.email?.pass)}
        />
        <TextFormField name="email.from" label="Send from" placeholder="alerts@example.com" />
        <SwitchFormField name="email.secure" label="Use a secure connection (TLS)" />
        <FormFullWidth>
          <TextFormField
            name="email.recipients"
            label="Recipients"
            placeholder="you@example.com, team@example.com"
            description="Separate addresses with commas."
          />
        </FormFullWidth>
        <TestChannelButton channel="email" label="email" />
      </FormSection>

      <FormSection title="Webhook" description="Post alerts as JSON to your own endpoint.">
        <FormFullWidth>
          <SwitchFormField name="webhook.enabled" label="Send alerts to a webhook" />
        </FormFullWidth>
        <FormFullWidth>
          <TextFormField
            name="webhook.url"
            label="URL"
            type="url"
            placeholder="https://your-server.com/hook"
            description={savedHint(config.webhook?.url)}
          />
        </FormFullWidth>
        <TestChannelButton channel="webhook" label="your webhook" />
      </FormSection>

      <div className="grid grid-cols-1">
        <Can policy="CanManageNotifications">
          <FormActions submitLabel="Save notifications" onCancel={() => form.reset()} className="md:col-span-1" />
        </Can>
      </div>
    </Form>
  );
}

function NotificationsSkeleton() {
  return (
    <div className="space-y-8" role="status" aria-label="Loading notification settings">
      {[0, 1, 2].map((section) => (
        <div key={section} className="space-y-4">
          <Skeleton height={24} width="20%" />
          <Skeleton height={44} rounded="rounded-lg" />
          <Skeleton height={44} rounded="rounded-lg" />
        </div>
      ))}
    </div>
  );
}

export const NotificationsTab: React.FC = () => {
  const query = useQuery(getNotificationConfigOptions());
  return (
    <QueryBoundary query={query} label="your notification settings" loading={<NotificationsSkeleton />}>
      {(data) => <NotificationsForm config={data.config} />}
    </QueryBoundary>
  );
};
